import React, { useState } from 'react';
import { Navigate, Switch } from '@common/components';
import { PrivateRoute } from '@routes/private-route';
import {
  ORGANIZATION_ADMIN_MENU,
  OrganizationAdminVerticalNavMenu,
} from 'works/pages/organization/admin-settings/organization-admin-vertical-nav-menu/organization-admin-vertical-nav-menu';
import { OnboardingListTemplatesPage } from 'works/pages/organization/admin-settings/templates-page/onboarding-list-templates';
import { OnboardingListTemplateNewPage } from 'works/pages/organization/admin-settings/templates-page/onboarding-list-templates/onboarding-list-template-new';
import { OnboardingListsTemplateEditPage } from 'works/pages/organization/admin-settings/templates-page/onboarding-list-templates/onboarding-lists-template-edit';
import { StaffingTemplatesPage } from 'works/pages/organization/admin-settings/templates-page/staffing-templates';
import { TemplatesLandingPage } from 'works/pages/organization/admin-settings/templates-page/templates-landing';
import { ApprovalsPage } from '@approvals/pages/organization/approvals-page';
import { OnboardingListsTemplateItemsPage } from 'works/pages/organization/admin-settings/templates-page/onboarding-list-templates/onboarding-lists-template-items';
import { OnboardingListsTemplateItemsConfirmUpdatePage } from 'works/pages/organization/admin-settings/templates-page/onboarding-list-templates/onboarding-lists-template-items/onboarding-lists-template-items-confirm-update-page';
import {
  AdminOrganizationAddUnitsPage,
  AdminOrganizationFacilityPage,
  AdminOrganizationStructurePage,
  AdminOrganizationUnitPage,
  AdminRatesLongTermPage,
  AdminSuppliersManagementPage,
  RatesLongTermCreationSteps,
  SuppliersManagementBulkLocationsUpdateSteps,
  SuppliersManagementInvitationSteps,
  SuppliersManagementLocationsUpdateSteps,
  SuppliersManagementNewSupplier,
} from '@institutions/pages/organization';
import { AdminTimesheetHolidays } from '@timekeeping/pages/organization';
import { ComplianceLinkAssignments } from '@onboarding/pages/compliance-link-assignments';
import { NoMatch } from '@routes/no-match';
import {
  AdminAllowListsPage,
  AdminIncentiveRatesPage,
  AdminWorkforceGroupPage,
  AdminWorkforceGroupsPage,
  IncentivesUpdateSteps,
} from '@scheduling/pages/organization';
import { UuidRegex } from '@common/services';
import { AdminOrganizationBillingPage } from '@pay-bill/pages/organization/admin-organization-billing-page';
import {
  AdminMembersPage,
  MembersAddEditSteps,
  MembersAddMultipleSteps,
  MembersUpdateLocationsSteps,
} from '@identity/pages';
import { useCurrentMembership } from '@identity/hooks';

const permissionKeys = [
  'canShowAdminApprovalsItem',
  'canShowAdminStaffingRequestTemplates',
  'canShowAdminWorksListTemplates',
  'canShowAdminRatesOnDemandTab',
  'canShowAdminRatesLongTermTab',
  'canShowAdminMembers',
  'canShowAdminSuppliers',
  'canShowAdminOrganizationStructure',
  'canShowAdminTimekeepingHolidays',
  'canShowAdminGroupsTab',
  'canShowAdminBilling',
  'canShowOrganizationOnDemandSettings',
] as const;

type PermissionKey = (typeof permissionKeys)[number];

type PermissionsSet = Record<PermissionKey, boolean>;

export function OrganizationAdminSettingsRoutes() {
  const [shouldShowMenu, setShouldShowMenu] = useState(true);
  const currentMembership = useCurrentMembership();

  const permissions = permissionKeys.reduce((acc, key) => {
    acc[key] = currentMembership[key] ?? false;
    return acc;
  }, {} as PermissionsSet);

  return (
    <OrganizationAdminVerticalNavMenu
      permissions={permissions}
      hideMenu={!shouldShowMenu}
    >
      <Switch>
        <PrivateRoute
          exact
          path="/works/admin/allow-lists"
          menuItem="admin"
          component={AdminAllowListsPage}
          onRender={() => setShouldShowMenu(true)}
          permitted={permissions.canShowOrganizationOnDemandSettings}
        />
        <PrivateRoute
          exact
          path="/works/admin/templates"
          menuItem="redirect"
          component={TemplatesLandingPage}
          onRender={() => setShouldShowMenu(true)}
        />
        <PrivateRoute
          path="/works/admin/templates/staffing-request"
          component={StaffingTemplatesPage}
          menuItem="admin"
          secondaryMenuItem="staffing-request-templates"
          onRender={() => setShouldShowMenu(true)}
          permitted={permissions.canShowAdminStaffingRequestTemplates}
        />
        <PrivateRoute
          exact
          path={`${ORGANIZATION_ADMIN_MENU.LOCATIONS.ROUTE}/unit/:organizationUnitId`}
          component={({ match: { params } }) => (
            <Navigate
              to={`${ORGANIZATION_ADMIN_MENU.LOCATIONS.ROUTE}/unit/${params.organizationUnitId}/unit-details`}
            />
          )}
          permitted={permissions.canShowAdminOrganizationStructure}
        />
        <PrivateRoute
          path={`${ORGANIZATION_ADMIN_MENU.LOCATIONS.ROUTE}/unit/:organizationUnitId(${UuidRegex})/:tabKey(unit-details|shifts|integration)`}
          component={AdminOrganizationUnitPage}
          onRender={() => setShouldShowMenu(false)}
          permitted={permissions.canShowAdminOrganizationStructure}
        />
        <PrivateRoute
          path={`${ORGANIZATION_ADMIN_MENU.LOCATIONS.ROUTE}/add-units`}
          component={AdminOrganizationAddUnitsPage}
          onRender={() => setShouldShowMenu(false)}
          permitted={permissions.canShowAdminOrganizationStructure}
        />
        <PrivateRoute
          exact
          path={`${ORGANIZATION_ADMIN_MENU.LOCATIONS.ROUTE}/facility/:organizationFacilityId(${UuidRegex})`}
          component={({ match: { params } }) => (
            <Navigate
              to={`${ORGANIZATION_ADMIN_MENU.LOCATIONS.ROUTE}/facility/${params.organizationFacilityId}/details`}
            />
          )}
          permitted={permissions.canShowAdminOrganizationStructure}
        />
        <PrivateRoute
          path={`${ORGANIZATION_ADMIN_MENU.LOCATIONS.ROUTE}/facility/:organizationFacilityId(${UuidRegex})/:page(details|groups)`}
          menuItem="admin"
          component={AdminOrganizationFacilityPage}
          onRender={() => setShouldShowMenu(false)}
          permitted={permissions.canShowAdminOrganizationStructure}
        />
        <PrivateRoute
          path={ORGANIZATION_ADMIN_MENU.LOCATIONS.ROUTE}
          menuItem="admin"
          component={AdminOrganizationStructurePage}
          onRender={() => setShouldShowMenu(true)}
          permitted={permissions.canShowAdminOrganizationStructure}
        />
        <PrivateRoute
          path={`${ORGANIZATION_ADMIN_MENU.GROUPS.ROUTE}/:workforceGroupId(${UuidRegex})/:tabKey(details|staff|locations|rates)`}
          component={AdminWorkforceGroupPage}
          onRender={() => setShouldShowMenu(false)}
          permitted={permissions.canShowAdminGroupsTab}
        />
        <PrivateRoute
          path={ORGANIZATION_ADMIN_MENU.GROUPS.ROUTE}
          component={AdminWorkforceGroupsPage}
          menuItem="admin"
          onRender={() => setShouldShowMenu(true)}
          permitted={permissions.canShowAdminGroupsTab}
        />
        <PrivateRoute
          exact
          path={`/works/admin/templates/compliance-list/:id(${UuidRegex})/items`}
          component={OnboardingListsTemplateItemsPage}
          menuItem="admin"
          secondaryMenuItem="compliance-list-template-item-page"
          onRender={() => setShouldShowMenu(false)}
          permitted={permissions.canShowAdminWorksListTemplates}
        />
        <PrivateRoute
          exact
          path={`/works/admin/templates/compliance-list/:id(${UuidRegex})/items/confirm-updates`}
          component={OnboardingListsTemplateItemsConfirmUpdatePage}
          menuItem="admin"
          secondaryMenuItem="compliance-list-template-item-page"
          onRender={() => setShouldShowMenu(false)}
          permitted={permissions.canShowAdminWorksListTemplates}
        />
        <PrivateRoute
          path={`/works/admin/templates/compliance-list/:id(${UuidRegex})/edit`}
          component={OnboardingListsTemplateEditPage}
          menuItem="admin"
          secondaryMenuItem="compliance-list-templates"
          onRender={() => setShouldShowMenu(true)}
          permitted={permissions.canShowAdminWorksListTemplates}
        />
        <PrivateRoute
          path={`/works/admin/templates/compliance-list/:id(${UuidRegex})/link-assignments`}
          component={ComplianceLinkAssignments}
          menuItem="admin"
          secondaryMenuItem="compliance-list-template-link-assignments-page"
          onRender={() => setShouldShowMenu(false)}
          permitted={permissions.canShowAdminWorksListTemplates}
        />
        <PrivateRoute
          path="/works/admin/templates/compliance-list/new"
          component={OnboardingListTemplateNewPage}
          menuItem="admin"
          secondaryMenuItem="compliance-list-templates"
          onRender={() => setShouldShowMenu(false)}
          permitted={permissions.canShowAdminWorksListTemplates}
        />
        <PrivateRoute
          path="/works/admin/templates/compliance-list"
          component={OnboardingListTemplatesPage}
          menuItem="admin"
          secondaryMenuItem="compliance-list-templates"
          onRender={() => setShouldShowMenu(true)}
          permitted={permissions.canShowAdminWorksListTemplates}
        />
        <PrivateRoute
          path="/works/admin/approvals/:organizationBusinessId"
          component={ApprovalsPage}
          menuItem="admin"
          secondaryMenuItem="approvals-page"
          onRender={() => setShouldShowMenu(true)}
          permitted={permissions.canShowAdminApprovalsItem}
        />
        <PrivateRoute
          path="/works/admin/approvals"
          component={ApprovalsPage}
          menuItem="admin"
          secondaryMenuItem="approvals-page"
          onRender={() => setShouldShowMenu(true)}
          permitted={permissions.canShowAdminApprovalsItem}
        />
        <PrivateRoute
          path="/works/admin/rates/incentives/update"
          component={IncentivesUpdateSteps}
          menuItem="admin"
          secondaryMenuItem="rates-on-demand-page"
          onRender={() => setShouldShowMenu(false)}
          permitted={permissions.canShowAdminRatesOnDemandTab}
        />
        <PrivateRoute
          path="/works/admin/rates/incentives"
          component={AdminIncentiveRatesPage}
          menuItem="admin"
          secondaryMenuItem="rates-on-demand-page"
          onRender={() => setShouldShowMenu(true)}
          permitted={permissions.canShowAdminRatesOnDemandTab}
        />
        <PrivateRoute
          path="/works/admin/rates/long-term/create"
          component={RatesLongTermCreationSteps}
          menuItem="admin"
          secondaryMenuItem="rate-creation-page"
          onRender={() => setShouldShowMenu(false)}
          permitted={permissions.canShowAdminRatesLongTermTab}
        />
        <PrivateRoute
          path="/works/admin/rates/long-term"
          component={AdminRatesLongTermPage}
          menuItem="admin"
          secondaryMenuItem="rate-long-term-page"
          onRender={() => setShouldShowMenu(true)}
          permitted={permissions.canShowAdminRatesLongTermTab}
        />
        <PrivateRoute
          menuItem="admin"
          component={SuppliersManagementInvitationSteps}
          path="/works/admin/suppliers/invitation/:supplierId?"
          secondaryMenuItem="supplier-invitation-steps"
          onRender={() => setShouldShowMenu(false)}
          permitted={permissions.canShowAdminSuppliers}
        />
        <PrivateRoute
          menuItem="admin"
          component={SuppliersManagementNewSupplier}
          path="/works/admin/suppliers/new"
          secondaryMenuItem="supplier"
          onRender={() => setShouldShowMenu(false)}
          permitted={permissions.canShowAdminSuppliers}
        />
        <PrivateRoute
          menuItem="admin"
          component={SuppliersManagementLocationsUpdateSteps}
          path="/works/admin/suppliers/locations-update/:supplierId"
          secondaryMenuItem="supplier-permission-update-steps"
          onRender={() => setShouldShowMenu(false)}
          permitted={permissions.canShowAdminSuppliers}
        />
        <PrivateRoute
          menuItem="admin"
          component={SuppliersManagementLocationsUpdateSteps}
          path="/works/admin/suppliers/activation/:supplierId"
          secondaryMenuItem="supplier-activation-steps"
          onRender={() => setShouldShowMenu(false)}
          permitted={permissions.canShowAdminSuppliers}
        />
        <PrivateRoute
          menuItem="admin"
          component={SuppliersManagementBulkLocationsUpdateSteps}
          path="/works/admin/suppliers/bulk-locations-update"
          secondaryMenuItem="bulk-supplier-permission-update-steps"
          onRender={() => setShouldShowMenu(false)}
          permitted={permissions.canShowAdminSuppliers}
        />
        <PrivateRoute
          menuItem="admin"
          component={AdminSuppliersManagementPage}
          path="/works/admin/suppliers"
          secondaryMenuItem="suppliers-page"
          onRender={() => setShouldShowMenu(true)}
          permitted={permissions.canShowAdminSuppliers}
        />
        <PrivateRoute
          menuItem="admin"
          component={MembersAddEditSteps}
          path={`${ORGANIZATION_ADMIN_MENU.USERS.ROUTE}/add`}
          secondaryMenuItem="members-page"
          onRender={() => setShouldShowMenu(false)}
          permitted={permissions.canShowAdminMembers}
        />
        <PrivateRoute
          menuItem="admin"
          component={MembersAddEditSteps}
          path={`${ORGANIZATION_ADMIN_MENU.USERS.ROUTE}/edit/:email`}
          secondaryMenuItem="members-page"
          onRender={() => setShouldShowMenu(false)}
          permitted={permissions.canShowAdminMembers}
        />
        <PrivateRoute
          menuItem="admin"
          component={MembersUpdateLocationsSteps}
          path={`${ORGANIZATION_ADMIN_MENU.USERS.ROUTE}/update-locations`}
          secondaryMenuItem="members-page"
          onRender={() => setShouldShowMenu(false)}
          permitted={permissions.canShowAdminMembers}
        />
        <PrivateRoute
          menuItem="admin"
          component={MembersAddMultipleSteps}
          path={`${ORGANIZATION_ADMIN_MENU.USERS.ROUTE}/upload`}
          secondaryMenuItem="members-page"
          onRender={() => setShouldShowMenu(false)}
          permitted={permissions.canShowAdminMembers}
        />
        <PrivateRoute
          menuItem="admin"
          component={AdminMembersPage}
          path={ORGANIZATION_ADMIN_MENU.USERS.ROUTE}
          secondaryMenuItem="members-page"
          onRender={() => setShouldShowMenu(true)}
          permitted={permissions.canShowAdminMembers}
        />
        <PrivateRoute
          exact
          path="/works/admin/rates"
          menuItem="redirect"
          component={() => <Navigate to="/works/admin/rates/long-term" />}
        />
        <PrivateRoute
          exact
          path="/works/admin"
          menuItem="redirect"
          component={() =>
            permissions.canShowOrganizationOnDemandSettings ? (
              <Navigate to={ORGANIZATION_ADMIN_MENU.ALLOW_LISTS.ROUTE} />
            ) : (
              <Navigate to={ORGANIZATION_ADMIN_MENU.APPROVALS.ROUTE} />
            )
          }
        />
        <PrivateRoute
          exact
          menuItem="admin"
          component={AdminOrganizationBillingPage}
          path={ORGANIZATION_ADMIN_MENU.BILLING.ROUTE}
          secondaryMenuItem="billing-page"
          onRender={() => setShouldShowMenu(true)}
          permitted={permissions.canShowAdminBilling}
        />
        <PrivateRoute
          exact
          menuItem="admin"
          component={AdminTimesheetHolidays}
          path={ORGANIZATION_ADMIN_MENU.TIMEKEEPING.ROUTE}
          secondaryMenuItem="timesheets-page"
          onRender={() => setShouldShowMenu(true)}
          permitted={permissions.canShowAdminTimekeepingHolidays}
        />
        {/**
         * [BEGIN] Legacy URLs redirecting
         * */}
        <PrivateRoute
          path="/works/admin/organization-structure/:path*"
          component={({ match }) => (
            <Navigate
              to={`${ORGANIZATION_ADMIN_MENU.LOCATIONS.ROUTE}/${
                match.params.path || ''
              }`}
            />
          )}
        />
        <PrivateRoute
          exact
          path="/works/admin/organization-units"
          component={() => (
            <Navigate to={ORGANIZATION_ADMIN_MENU.LOCATIONS.ROUTE} />
          )}
        />
        <PrivateRoute
          path="/works/admin/organization-units/*"
          component={({ match }) => (
            <Navigate
              to={`${ORGANIZATION_ADMIN_MENU.LOCATIONS.ROUTE}/unit/${match.params[0]}`}
            />
          )}
        />
        <PrivateRoute
          exact
          path="/works/admin/staffing_request_templates"
          menuItem="redirect"
          component={() => (
            <Navigate to="/works/admin/templates/staffing-request" />
          )}
        />
        <PrivateRoute
          exact
          path={`/works/admin/compliance_list_templates/:id(${UuidRegex})/edit`}
          menuItem="redirect"
          component={({ match }) => (
            <Navigate
              to={`/works/admin/templates/compliance-list/${match.params.id}/edit`}
            />
          )}
        />
        <PrivateRoute
          exact
          path="/works/admin/compliance_list_templates/new"
          menuItem="redirect"
          component={({ match }) => (
            <Navigate
              to={`/works/admin/templates/compliance-list/${match.params.id}/new`}
            />
          )}
        />
        <PrivateRoute
          exact
          path={`/works/admin/compliance_list_templates/:id(${UuidRegex})/items`}
          menuItem="redirect"
          component={({ match }) => (
            <Navigate
              to={`/works/admin/templates/compliance-list/${match.params.id}/items`}
            />
          )}
        />
        <PrivateRoute
          exact
          path="/works/admin/compliance_list_templates"
          menuItem="redirect"
          component={() => (
            <Navigate to="/works/admin/templates/compliance-list" />
          )}
        />
        <PrivateRoute
          path="/works/admin/members/:path*"
          component={({ match }) => (
            <Navigate
              to={`${ORGANIZATION_ADMIN_MENU.USERS.ROUTE}/${
                match.params.path || ''
              }`}
            />
          )}
        />
        <PrivateRoute
          exact
          path="/works/admin/timesheet-holidays"
          component={() => (
            <Navigate to={ORGANIZATION_ADMIN_MENU.TIMEKEEPING.ROUTE} />
          )}
        />
        {/**
         * [END] Legacy URLs redirecting
         * */}
        <PrivateRoute component={NoMatch} menuItem={null} />
      </Switch>
    </OrganizationAdminVerticalNavMenu>
  );
}
